import { CONTENT_TYPES } from '../../utils/qrFormats';
import PhoneFields from './PhoneFields';

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20';

const labelClass = 'mb-1.5 block text-sm font-medium text-slate-700';

function FieldGroup({ title, children }) {
  return (
    <div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </p>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function ContentInputs({ contentType, fields, updateField }) {
  switch (contentType) {
    case CONTENT_TYPES.url:
      return (
        <div>
          <label htmlFor="qr-url" className={labelClass}>
            Website URL
          </label>
          <input
            id="qr-url"
            type="url"
            className={inputClass}
            placeholder="https://example.com"
            value={fields.url}
            onChange={(e) => updateField('url', e.target.value)}
          />
        </div>
      );
    case CONTENT_TYPES.text:
      return (
        <div>
          <label htmlFor="qr-text" className={labelClass}>
            Plain text
          </label>
          <textarea
            id="qr-text"
            rows={4}
            className={inputClass}
            placeholder="Enter any text for your QR code"
            value={fields.text}
            onChange={(e) => updateField('text', e.target.value)}
          />
        </div>
      );
    case CONTENT_TYPES.email:
      return (
        <div className="space-y-4">
          <div>
            <label htmlFor="qr-email" className={labelClass}>
              Email address
            </label>
            <input
              id="qr-email"
              type="email"
              className={inputClass}
              placeholder="name@example.com"
              value={fields.emailAddress}
              onChange={(e) => updateField('emailAddress', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="qr-subject" className={labelClass}>
              Subject (optional)
            </label>
            <input
              id="qr-subject"
              type="text"
              className={inputClass}
              value={fields.emailSubject}
              onChange={(e) => updateField('emailSubject', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="qr-body" className={labelClass}>
              Message (optional)
            </label>
            <textarea
              id="qr-body"
              rows={3}
              className={inputClass}
              value={fields.emailBody}
              onChange={(e) => updateField('emailBody', e.target.value)}
            />
          </div>
        </div>
      );
    case CONTENT_TYPES.call:
      return (
        <PhoneFields
          idPrefix="call"
          countryCode={fields.callCountryCode}
          phone={fields.callPhone}
          onCountryCodeChange={(v) => updateField('callCountryCode', v)}
          onPhoneChange={(v) => updateField('callPhone', v)}
        />
      );
    case CONTENT_TYPES.sms:
      return (
        <PhoneFields
          idPrefix="sms"
          countryCode={fields.smsCountryCode}
          phone={fields.smsPhone}
          onCountryCodeChange={(v) => updateField('smsCountryCode', v)}
          onPhoneChange={(v) => updateField('smsPhone', v)}
          message={fields.smsMessage}
          onMessageChange={(v) => updateField('smsMessage', v)}
          showMessage
        />
      );
    case CONTENT_TYPES.vcard:
      return (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="vcard-first" className={labelClass}>
                First name
              </label>
              <input
                id="vcard-first"
                type="text"
                className={inputClass}
                value={fields.vcardFirstName}
                onChange={(e) => updateField('vcardFirstName', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="vcard-last" className={labelClass}>
                Last name
              </label>
              <input
                id="vcard-last"
                type="text"
                className={inputClass}
                value={fields.vcardLastName}
                onChange={(e) => updateField('vcardLastName', e.target.value)}
              />
            </div>
          </div>
          <FieldGroup title="Contact information">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="vcard-phone" className={labelClass}>
                  Phone number
                </label>
                <input
                  id="vcard-phone"
                  type="tel"
                  className={inputClass}
                  value={fields.vcardPhone}
                  onChange={(e) => updateField('vcardPhone', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="vcard-mobile" className={labelClass}>
                  Mobile
                </label>
                <input
                  id="vcard-mobile"
                  type="tel"
                  className={inputClass}
                  value={fields.vcardMobile}
                  onChange={(e) => updateField('vcardMobile', e.target.value)}
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="vcard-email" className={labelClass}>
                  E-mail
                </label>
                <input
                  id="vcard-email"
                  type="email"
                  className={inputClass}
                  value={fields.vcardEmail}
                  onChange={(e) => updateField('vcardEmail', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="vcard-website" className={labelClass}>
                  Website (URL)
                </label>
                <input
                  id="vcard-website"
                  type="url"
                  className={inputClass}
                  placeholder="https://example.com"
                  value={fields.vcardWebsite}
                  onChange={(e) => updateField('vcardWebsite', e.target.value)}
                />
              </div>
            </div>
          </FieldGroup>
          <FieldGroup title="Company information">
            <div>
              <label htmlFor="vcard-company" className={labelClass}>
                Company
              </label>
              <input
                id="vcard-company"
                type="text"
                className={inputClass}
                value={fields.vcardCompany}
                onChange={(e) => updateField('vcardCompany', e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="vcard-title" className={labelClass}>
                  Job title
                </label>
                <input
                  id="vcard-title"
                  type="text"
                  className={inputClass}
                  value={fields.vcardJobTitle}
                  onChange={(e) => updateField('vcardJobTitle', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="vcard-fax" className={labelClass}>
                  Fax
                </label>
                <input
                  id="vcard-fax"
                  type="tel"
                  className={inputClass}
                  value={fields.vcardFax}
                  onChange={(e) => updateField('vcardFax', e.target.value)}
                />
              </div>
            </div>
          </FieldGroup>
        </div>
      );
    case CONTENT_TYPES.wifi:
      return (
        <div className="space-y-4">
          <div>
            <label htmlFor="qr-ssid" className={labelClass}>
              Network name
            </label>
            <input
              id="qr-ssid"
              type="text"
              className={inputClass}
              placeholder="SSID"
              value={fields.wifiSsid}
              onChange={(e) => updateField('wifiSsid', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="qr-encryption" className={labelClass}>
              Type of encryption
            </label>
            <select
              id="qr-encryption"
              className={inputClass}
              value={fields.wifiEncryption}
              onChange={(e) => updateField('wifiEncryption', e.target.value)}
            >
              <option value="WPA">WPA / WPA2</option>
              <option value="WEP">WEP</option>
              <option value="nopass">None</option>
            </select>
          </div>
          <div>
            <label htmlFor="qr-wifi-pass" className={labelClass}>
              Password
            </label>
            <input
              id="qr-wifi-pass"
              type="password"
              className={inputClass}
              placeholder="Wi-Fi password"
              value={fields.wifiPassword}
              onChange={(e) => updateField('wifiPassword', e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              checked={fields.wifiHidden}
              onChange={(e) => updateField('wifiHidden', e.target.checked)}
            />
            Hidden network
          </label>
        </div>
      );
    default:
      return null;
  }
}

